/**
 * offeredModalities.js — PROD-644 (patient part, P2). Which visit modalities a physician OFFERS, so
 * the patient is offered only those: ShareConversation (the first request), ScheduleFollowupScreen
 * and the CaseHome reschedule.
 *
 * ★★★ THE DEFECT (Alex 2026-10-06: "if the physician only takes in-person appointments, can the
 *   patient only request in-person?" — measured on a0a93405: no). ShareConversation.doctorAcceptsVideo
 *   was the only reader of the physician's settings and fell back to TRUE whenever the doctor object
 *   lacked them (most paths); ScheduleFollowupScreen gated video only by the encounter category; the
 *   CaseHome reschedule always offered both. In-person was never hidden anywhere.
 *
 * ★★ THE RULE IS THE MD PANEL'S (md10a src/utils/offeredModalities.js, check-appt-modality) AND THE
 *   API'S (D6b _offered_modalities), so the three never disagree:
 *     new patient visits ('initial'): newEnabled, newInPerson, newVirtual
 *     every later visit:              followupEnabled, followupInPerson, followupVirtual
 *   In-person is offered when the visit type is on and its InPerson flag is NOT explicitly false (a
 *   row written before the InPerson flags existed carries none: in-person, PROD-663). Video only when
 *   the visit type is on and its Virtual flag is true. categoryAllowsVideo still applies on top.
 *
 * ★★ WHERE THE PATIENT APP READS THE SETTINGS (availabilityOfDoctor). The doctor payloads the app
 *   gets say it in one of two explicit shapes, through the token list (video only, below), or not
 *   at all:
 *     (a) the flags themselves: doctor.appointmentTypes / doctor.availability_schedule.appointmentTypes
 *         (an object, or the JSON string the column can hold) -> the md10a rule above;
 *     (b) the API's decided answer, the shape of D6b's _offered_modalities(schedule, category):
 *         doctor.offered_modalities (or offeredModalities) = { new: { known, in_person, video },
 *         followup: { ... } } ('initial' / 'followUp' accepted as keys). Only an explicit false
 *         switches a modality off; a kind with known false or no entry does not say.
 *   ★★★ THE TOKEN LIST DECIDES VIDEO ONLY, NEVER IN-PERSON (PROD-644 repair rounds 1 and 2, review
 *     MUST-1 / SHOULD-A). doctor.appointment_types (['new_in_person', 'new_virtual', ...], full-profile)
 *     only ever names what is ON, and the two halves of it mean different things:
 *       - in-person: the live API c71d3cb adds new_in_person only when newInPerson is truthy
 *         (`if appt_config.get('newInPerson')`), so a legacy row with no InPerson flag sends no
 *         in-person token although in-person IS offered (PROD-663). An absent in-person token is
 *         ambiguous on c71d3cb, so the list never decides in-person (no InPerson key is written:
 *         inPersonFlagOn reads that as offered).
 *       - video: both c71d3cb and D6b add <kind>_virtual exactly when <kind>Virtual is true (c71d3cb
 *         by truthiness, D6b `is True`), and the md10a / D6b rule offers video only when Virtual is
 *         true. So in a NON-EMPTY full-profile list an absent <kind>_virtual means video is OFF for
 *         that kind (newVirtual -> new_virtual, followupVirtual -> followup_virtual).
 *     The list counts only when it is the full-profile shape: at least one new_* / followup_* /
 *     procedure_* token. The doctors list's legacy column (unprefixed 'video', 'in_person', ...) and
 *     an empty list do not say.
 *   Precedence: the flags object (a) decides both kinds when present; otherwise, per visit kind, an
 *   explicit entry in (b), else the full-profile token list (video only), else nothing.
 *   When the payload does not say, the answer is TODAY'S behaviour (restricted: false) and each
 *   screen keeps its pre-644 rule. The API refuses a modality the physician does not offer (D6b).
 *
 * Pure, no imports: scripts/check-visit-modality.mjs executes it over the matrix.
 */

const visitKind = (category) => (category === 'initial' || category === 'new' ? 'new' : 'followup')

/** The one in-person rule (md10a PROD-663): true unless that visit type's InPerson flag is explicitly false. */
export const inPersonFlagOn = (appointmentTypes, kind) =>
  !!appointmentTypes && typeof appointmentTypes === 'object' && appointmentTypes[`${kind}InPerson`] !== false

/** offeredModalities(availability, category) -> { known, inPerson, video } (md10a, unchanged). */
export function offeredModalities(availability, category) {
  const t = availability && typeof availability === 'object' ? availability.appointmentTypes : null
  if (!t || typeof t !== 'object') return { known: false, inPerson: false, video: false }
  const k = visitKind(category)
  const on = t[`${k}Enabled`] === true
  return { known: true, inPerson: on && inPersonFlagOn(t, k), video: on && t[`${k}Virtual`] === true }
}

/**
 * allowedVisitTypes({ availability, category, videoAllowedForCategory }) -> { types, restricted }
 *   types: the subset of ['in_person', 'video'] offered, never empty
 *   restricted: true when the physician's own settings decided it (false: the pre-644 fallback)
 */
export function allowedVisitTypes({ availability, category, videoAllowedForCategory }) {
  const o = offeredModalities(availability, category)
  const byPhysician = []
  if (o.inPerson) byPhysician.push('in_person')
  if (o.video && videoAllowedForCategory) byPhysician.push('video')
  if (o.known && byPhysician.length > 0) return { types: byPhysician, restricted: true }
  return { types: videoAllowedForCategory ? ['in_person', 'video'] : ['in_person'], restricted: false }
}

/** { appointmentTypes } read from a patient-app doctor object, or null when it does not say. */
export function availabilityOfDoctor(doctor) {
  if (!doctor || typeof doctor !== 'object') return null
  const asObject = (v) => {
    if (typeof v === 'string') { try { v = JSON.parse(v) } catch { return null } }
    return v && typeof v === 'object' && !Array.isArray(v) ? v : null
  }
  if (asObject(doctor.appointmentTypes)) return { appointmentTypes: asObject(doctor.appointmentTypes) }
  const sched = asObject(doctor.availability_schedule) || asObject(doctor.availabilitySchedule)
  if (sched && asObject(sched.appointmentTypes)) return { appointmentTypes: asObject(sched.appointmentTypes) }
  // Per visit kind: (b) the API's decided answer (D6b _offered_modalities shape) decides both
  //   modalities, explicit false only switches off, known false / no entry does not say;
  //   otherwise (c) a full-profile token list decides VIDEO only (header: an absent <kind>_virtual
  //   is off, in-person is never decided by the list).
  const om = asObject(doctor.offered_modalities) || asObject(doctor.offeredModalities)
  const entry = (k) => {
    if (!om) return null
    const keys = k === 'new' ? ['new', 'initial'] : ['followup', 'followUp', 'follow_up']
    for (const key of keys) { const e = asObject(om[key]); if (e) return e }
    return null
  }
  const tokens = Array.isArray(doctor.appointment_types)
    ? doctor.appointment_types.filter((x) => typeof x === 'string')
    : []
  // ★ PROD-681 (3): the token list is read only from the FULL-PROFILE payload. orella-api's
  //   get_doctor_full_profile writes appointment_types together with durations
  //   { new, followup, procedure } (numbers, _availability_minutes); the doctors list copies a
  //   column of the same name (phantom on c71d3cb / D6b) beside no durations. A prefixed token
  //   array in any payload without that shape does not say.
  const d = doctor.durations
  const isFullProfile = !!d && typeof d === 'object' && ['new', 'followup', 'procedure'].every((k) => typeof d[k] === 'number')
  const fullProfileList = isFullProfile && tokens.some((x) => /^(new|followup|procedure)_(in_person|virtual)$/.test(x))
  const t = {}
  let says = false
  for (const k of ['new', 'followup']) {
    const e = entry(k)
    if (e && e.known !== false) {
      says = true
      t[`${k}Enabled`] = true
      t[`${k}InPerson`] = e.in_person !== false
      t[`${k}Virtual`] = e.video !== false
    } else if (fullProfileList) {
      says = true
      t[`${k}Enabled`] = true
      t[`${k}Virtual`] = tokens.includes(`${k}_virtual`)
    }
  }
  return says ? { appointmentTypes: t } : null
}

/** The patient-app entry point: allowedVisitTypes for a doctor object. */
export function allowedVisitTypesForDoctor({ doctor, category, videoAllowedForCategory = true }) {
  return allowedVisitTypes({ availability: availabilityOfDoctor(doctor), category, videoAllowedForCategory })
}
